'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {mediaFromWire, type FeatureItemsWireSection} from '../../model';
import {RichContent} from '../article';
import {FeatureItem} from '../home-sections/FeatureItem';
import {Section} from '../primitives';
import {EyebrowHeading} from './EyebrowHeading';
import {OVERVIEW_ASPECT, SectionMedia} from './SectionMedia';

export interface AboutFeatureItemsSectionProps {
  title: string;
  featureItems: FeatureItemsWireSection['featureItems'];
  sectionId?: string;
}

export function AboutFeatureItemsSection({title, featureItems, sectionId}: AboutFeatureItemsSectionProps) {
  const media = mediaFromWire(featureItems?.media);
  const eyebrow = featureItems?.displayLabel;
  const description = featureItems?.description;
  if (!media || !title || !description) {
    return null;
  }
  const items = featureItems.isFeatureItemsVisible
    ? (featureItems.featureItems ?? []).map(item => ({icon: item.icon, title: item.name, subtitle: item.subTitle}))
    : [];
  return (
    <Section tone="raised" sx={{paddingBlock: {xs: 4, md: 9}}}>
      <Container>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', md: '422fr 570fr'},
            gridTemplateAreas: {
              xs: '"head" "media" "copy" "items"',
              md: '". media" "head media" "copy media" "items media" ". media"',
            },
            gridTemplateRows: {md: '1fr auto auto auto 1fr'},
            columnGap: 13,
            rowGap: 3,
          }}
        >
          <Box sx={{gridArea: 'head'}}>
            <EyebrowHeading eyebrow={eyebrow} title={title} />
          </Box>
          <Box sx={{gridArea: 'copy', maxWidth: 520}}>
            <RichContent html={description} collapsedLines={5} />
          </Box>
          <Box sx={{gridArea: 'items', display: 'flex', flexDirection: 'column', gap: 2.5}}>
            {items.map(item => (
              <FeatureItem key={item.title} icon={item.icon} title={item.title} subtitle={item.subtitle} />
            ))}
          </Box>
          <Box sx={{gridArea: 'media', alignSelf: {md: 'center'}, width: '100%'}}>
            <SectionMedia
              media={media}
              alt={media.alt ?? title}
              aspectRatio={OVERVIEW_ASPECT}
              elevated
              accent
              sectionName={title}
              sectionId={sectionId}
            />
          </Box>
        </Box>
      </Container>
    </Section>
  );
}

AboutFeatureItemsSection.displayName = 'AboutFeatureItemsSection';
